import type { KitLyricLayer } from '../fullVideoPlan';
import type { AssetStyleId } from '../assetStyles';
import type { KitFontId, ThemeTypeStyle } from '../releaseThemes';

import { LONG_VIDEO_END_CARD_SEC, longVideoEndPose } from './longVideoEndCard';
import { fitStoryLine, type StoryBox } from './storyLayout';
import { storyFrameOf } from './storyFrames';
import type { StoryStyle } from './storyTypes';
import { outCubic, progress, round3 } from './videoMotion';
import { wideWatermark } from './watermark';

/**
 * The lyric video (design `asset-redesign/y Lyric video - new`, owner answers 2026-10-08): the
 * approved lyric Story laid out for 16:9 (1920 x 1080). The cover in its theme frame with the title
 * one time on the left, the lyric column on the right with the lyric motion of the theme, the lyric
 * in the artist's own case. Four states:
 *
 * - the title card (0 s to 2 s before the first line): a large cover, "Lyric video", the title and
 *   the artist (no SmartLink, owner default);
 * - the lyrics: a small cover with the title and the artist under it, the column on the right;
 * - a rest (a gap of 6 s or more): the title card again, with three dots that light one per second
 *   in its last 3 s;
 * - the end card (from max(last sung end + 1 s, song end - 20 s)): the shared end card of the long
 *   videos (`longVideoEndCard.ts`: the cover, the bars, the id line, the pill by the date, the
 *   SmartLink and the Free watermark in the left column); the right half stays free for the YouTube
 *   end screen elements. When the song ends on a lyric, the video adds a silent tail of up to 10 s.
 *
 * Dense lines show as couplets (`lyricCouplets`). The cover kicks (1.04, 0.22 s) on the downbeats
 * of the title card, the choruses and the rests; the verses stay still. On Free the watermark shows
 * for the whole video. The renderer (`KitLyricVideo`) and the web Studio preview read these tables.
 * Pure.
 */

export const LYRIC_VIDEO_CANVAS = { width: 1920, height: 1080 } as const;

/** The YouTube content box (estimates; title bar and controls 150 px each). The end screen zone is `LONG_VIDEO_END_SCREEN_ZONE`. */
export const WIDE_CONTENT_BOX: StoryBox = { x: 120, y: 160, w: 1680, h: 760 };

/** The smallest lyric (about 15 points on a phone) and the smallest other text (about 8 points). */
export const LYRIC_VIDEO_MIN_LYRIC_PX = 72;
export const WIDE_MIN_TEXT_PX = 40;
/** The lyric starts at this size times the display factor of the theme, before the fit. */
export const LYRIC_VIDEO_LYRIC_PX = 104;

/** The lyric column: x 640 to 1780, the sung line ends at y 570, the next line waits from y 640. */
export const LYRIC_VIDEO_COLUMN = { x: 640, top: 180, bottom: 920, w: 1140, sungBottom: 570, gap: 70 } as const;

/** A gap of this many seconds or more brings the title card back (owner default 5 of page y). */
export const LYRIC_VIDEO_REST_GAP_SEC = 6;
/** The rest starts this long after the sung end and ends this long before the next line. */
export const LYRIC_VIDEO_REST_AFTER_SEC = 0.6;
export const LYRIC_VIDEO_REST_BEFORE_SEC = 1.5;
/** The title card ends this long before the first line. */
export const LYRIC_VIDEO_TITLE_BEFORE_SEC = 2;
/**
 * The end card: from the last sung end + 1 s, at the latest 20 s (`LONG_VIDEO_END_CARD_SEC`) before
 * the song end. It lasts 20 s; when the song ends too soon after the last lyric, a silent tail of up
 * to 10 s fills it (owner answer 1, 2026-10-08).
 */
export const LYRIC_VIDEO_END_AFTER_SEC = 1;
export const LYRIC_VIDEO_TAIL_MAX_SEC = 10;
/** Two dense lines make a couplet: both start less than 2.2 s before the next line, less than 1 s apart. */
export const LYRIC_COUPLET_MAX_LINE_SEC = 2.2;
export const LYRIC_COUPLET_MAX_GAP_SEC = 1;
/** A state change cross-fades and moves the cover over this time. */
export const WIDE_STATE_MOVE_SEC = 0.6;
/** The count-in dots. */
export const WIDE_REST_DOTS = 3;

/** The times of the four states, in video seconds. */
export interface LyricVideoStates {
  /** The title card ends here (2 s before the first line). */
  titleCardToSec: number;
  /** The rests: the title card again, from the sung end + 0.6 s to the next start - 1.5 s. */
  rests: Array<{ fromSec: number; toSec: number }>;
  endCardSec: number;
  /** The end of the master in the video (the song or its trim). */
  songEndSec: number;
  /** The video: the song, or longer by a silent tail (up to 10 s) for the end card. */
  durationSec: number;
}

/** The texts and the timing of one lyric video (`plan.wide`, kind `lyricVideo`). */
export interface KitLyricVideo {
  kind: 'lyricVideo';
  title: string;
  artist: string;
  /** "Lyric video" on the title card. */
  kicker: string;
  /** The pill by the date ("Pre-save now", "Listen now", "Coming soon"). */
  cta: string;
  /** The SmartLink address with no scheme; null: none. */
  link: string | null;
  states: LyricVideoStates;
  /** The cover kicks on these downbeats (the title card, the choruses, the rests), video seconds. */
  kickSecs: number[];
  posterSec: number;
}

/** A sung line in seconds of the video. */
export interface TimedLine {
  startSec: number;
  endSec: number;
}

/**
 * The states of a lyric video from its sung lines and the song end (video seconds). With no line
 * the whole song is the title card up to the end card. Pure.
 */
export function lyricVideoStates(lines: ReadonlyArray<TimedLine>, songEndSec: number): LyricVideoStates {
  const sorted = [...lines].sort((a, b) => a.startSec - b.startSec);
  const first = sorted[0];
  const last = sorted[sorted.length - 1];
  const rests: LyricVideoStates['rests'] = [];
  sorted.forEach((line, index) => {
    const next = sorted[index + 1];
    if (next === undefined || next.startSec - line.endSec < LYRIC_VIDEO_REST_GAP_SEC) {
      return;
    }
    const fromSec = round3(line.endSec + LYRIC_VIDEO_REST_AFTER_SEC);
    const toSec = round3(next.startSec - LYRIC_VIDEO_REST_BEFORE_SEC);
    if (toSec > fromSec) {
      rests.push({ fromSec, toSec });
    }
  });
  const lastEnd = last?.endSec ?? 0;
  const endCardSec = round3(Math.max(0, lastEnd + LYRIC_VIDEO_END_AFTER_SEC, songEndSec - LONG_VIDEO_END_CARD_SEC));
  const durationSec = round3(
    Math.max(songEndSec, Math.min(endCardSec + LONG_VIDEO_END_CARD_SEC, songEndSec + LYRIC_VIDEO_TAIL_MAX_SEC))
  );
  return {
    titleCardToSec: round3(
      first === undefined ? endCardSec : Math.max(0, first.startSec - LYRIC_VIDEO_TITLE_BEFORE_SEC)
    ),
    rests,
    endCardSec,
    songEndSec: round3(songEndSec),
    durationSec,
  };
}

/**
 * Dense lines as couplets (owner answer 2 of page y): lines `i` and `i + 1` make one item when both
 * start less than `LYRIC_COUPLET_MAX_LINE_SEC` before the line after them and line `i + 1` starts
 * less than `LYRIC_COUPLET_MAX_GAP_SEC` after the sung end of line `i`. The item keeps the words of
 * both lines with their own times; `coupletBreak` is the first word of the second line, so each
 * line keeps its own row. Pure.
 */
export function lyricCouplets(layers: ReadonlyArray<KitLyricLayer>, fps: number): KitLyricLayer[] {
  const sorted = [...layers].sort((a, b) => a.fromFrame - b.fromFrame);
  const out: KitLyricLayer[] = [];
  const maxLine = LYRIC_COUPLET_MAX_LINE_SEC * fps;
  const maxGap = LYRIC_COUPLET_MAX_GAP_SEC * fps;
  for (let index = 0; index < sorted.length; index += 1) {
    const one = sorted[index] as KitLyricLayer;
    const two = sorted[index + 1];
    const after = sorted[index + 2];
    const dense =
      two !== undefined &&
      two.fromFrame - one.fromFrame < maxLine &&
      (after === undefined ? two.sungToFrame - two.fromFrame : after.fromFrame - two.fromFrame) < maxLine &&
      two.fromFrame - one.sungToFrame < maxGap;
    if (!dense || two === undefined) {
      out.push(one);
      continue;
    }
    out.push({
      lineIndex: one.lineIndex,
      text: `${one.text} ${two.text}`,
      fromFrame: one.fromFrame,
      durationFrames: two.fromFrame + two.durationFrames - one.fromFrame,
      sungToFrame: two.sungToFrame,
      words: [...one.words, ...two.words],
      captionStyle: one.captionStyle,
      coupletBreak: one.words.length,
    });
    index += 1;
  }
  return out;
}

/**
 * The boxes of the title card and the lyrics, in px of 1920 x 1080 (the build spec of page y). The
 * end card is the shared end card of the long videos (`longVideoEndPose`).
 */
export const LYRIC_VIDEO_LAYOUT = {
  lyrics: { cover: { x: 150, y: 250, size: 360 }, textX: 150, textY: 650, textW: 440, titlePx: 64, artistPx: 40 },
  title: {
    cover: { x: 200, y: 240, size: 600 },
    textX: 900,
    textY: 300,
    textW: 860,
    kickerPx: 40,
    titlePx: 150,
    artistPx: 56,
    dotsY: 820,
  },
} as const;

/**
 * The style of a lyric video that code lays out (design `asset-redesign/y3 Lyric video - styles`):
 * `card` the cover in its theme frame (Cover card, design y), `ground` the blurred cover behind the
 * type with no cover card (Cover ground), `type` the theme ground with no cover (Type only). The
 * AI footage style keeps the footage path and never comes here.
 */
export type LyricVideoStyle = StoryStyle;

/** The lyric video style of an asset style: Cover ground is `ground`, Type only is `type`, else `card`. Pure. */
export function lyricVideoStyleOf(style: AssetStyleId | null | undefined): LyricVideoStyle {
  if (style === 'coverGround') {
    return 'ground';
  }
  return style === 'typeOnly' ? 'type' : 'card';
}

/** True when the title card and the lyrics show the cover card (the end card always shows the cover). */
export function lyricVideoShowsCover(style: LyricVideoStyle): boolean {
  return style === 'card';
}

/**
 * The text boxes of the title card and the lyrics with no cover card (Cover ground, Type only; design
 * y3), in px of 1920 x 1080. The lyric column stays at `LYRIC_VIDEO_COLUMN`.
 */
export const LYRIC_VIDEO_NO_COVER_LAYOUT = {
  lyrics: { textX: 150, textY: 250, textW: 440, titlePx: 88, artistPx: 44 },
  title: { textX: 200, textY: 300, textW: 1520, kickerPx: 40, titlePx: 172, artistPx: 64, dotsY: 820 },
} as const;

/** With no cover card the lyric is this much larger (design y3: "large type"), never under 72 px. */
export const LYRIC_VIDEO_NO_COVER_LYRIC_SCALE = 1.15;

/** The text boxes of the title card and the lyrics of a style. Pure. */
export function lyricVideoTextLayout(style: LyricVideoStyle): {
  lyrics: { textX: number; textY: number; textW: number; titlePx: number; artistPx: number };
  title: {
    textX: number;
    textY: number;
    textW: number;
    kickerPx: number;
    titlePx: number;
    artistPx: number;
    dotsY: number;
  };
} {
  if (lyricVideoShowsCover(style)) {
    const { lyrics, title } = LYRIC_VIDEO_LAYOUT;
    return { lyrics, title };
  }
  return LYRIC_VIDEO_NO_COVER_LAYOUT;
}

/** The lyric size of a style before the fit: 104 px times the display factor (x 1.15 with no cover card), 72 px or more. Pure. */
export function lyricVideoLyricPx(style: LyricVideoStyle, displayFactor: number): number {
  const scale = lyricVideoShowsCover(style) ? 1 : LYRIC_VIDEO_NO_COVER_LYRIC_SCALE;
  return round3(Math.max(LYRIC_VIDEO_MIN_LYRIC_PX, LYRIC_VIDEO_LYRIC_PX * displayFactor * scale));
}

/**
 * The Free watermark of a 16:9 video (owner rule 2026-10-08): the row lockup (design w0,
 * `wideWatermark`) on every frame, bottom left just under the content box (from y 932), clear of
 * every item of the title card and the lyrics and of the end screen zone. The YouTube controls cover
 * it only while the player is hovered or paused. On the end card it moves into the CTA group of the
 * shared end card (`LONG_VIDEO_WATERMARK_ROW`).
 */
export const WIDE_WATERMARK_BOX: StoryBox = (() => {
  const { x, y, lockup } = wideWatermark();
  return { x, y, w: lockup.w, h: lockup.h };
})();

export type WideStateKind = 'title' | 'lyrics' | 'rest' | 'end';

interface StateSpan {
  kind: WideStateKind;
  fromSec: number;
}

/** The spans of the states in time order (each lasts to the start of the next). Pure. */
export function lyricVideoSpans(states: LyricVideoStates): StateSpan[] {
  const spans: StateSpan[] = [{ kind: 'title', fromSec: Number.NEGATIVE_INFINITY }];
  if (states.titleCardToSec < states.endCardSec) {
    spans.push({ kind: 'lyrics', fromSec: states.titleCardToSec });
  }
  for (const rest of states.rests) {
    if (rest.fromSec >= states.endCardSec) {
      continue;
    }
    spans.push({ kind: 'rest', fromSec: rest.fromSec });
    spans.push({ kind: 'lyrics', fromSec: Math.min(rest.toSec, states.endCardSec) });
  }
  spans.push({ kind: 'end', fromSec: states.endCardSec });
  return spans.filter((span, index) => index === 0 || span.fromSec > (spans[index - 1]?.fromSec ?? -1) - 1e-9);
}

/** The pose of a 16:9 lyric video at one time. */
export interface LyricVideoPose {
  /** The weights of the layouts (they cross-fade; they sum to 1). */
  title: number;
  lyrics: number;
  end: number;
  /** The cover box between the layouts, and its kick scale. */
  cover: { x: number; y: number; size: number; scale: number };
  /** The count-in dots that are lit (0..3). */
  dotsLit: number;
  /** The kind of the current state. */
  state: WideStateKind;
}

const lerp = (a: number, b: number, share: number): number => a + (b - a) * share;

function layoutWeights(kind: WideStateKind): { title: number; lyrics: number; end: number } {
  if (kind === 'end') {
    return { title: 0, lyrics: 0, end: 1 };
  }
  return kind === 'lyrics' ? { title: 0, lyrics: 1, end: 0 } : { title: 1, lyrics: 0, end: 0 };
}

function coverOf(kind: WideStateKind, themeId: string, watermark: boolean): { x: number; y: number; size: number } {
  if (kind === 'end') {
    const { cover } = longVideoEndPose(themeId, watermark);
    return { x: cover.x, y: cover.y, size: cover.w };
  }
  return kind === 'lyrics' ? LYRIC_VIDEO_LAYOUT.lyrics.cover : LYRIC_VIDEO_LAYOUT.title.cover;
}

/** The kick of the cover at `t`: 1.04 on a kick time, back to 1 in 0.22 s. Pure. */
export function wideKickScale(t: number, kickSecs: ReadonlyArray<number>): number {
  let since = Number.POSITIVE_INFINITY;
  for (const at of kickSecs) {
    if (at <= t && t - at < since) {
      since = t - at;
    }
  }
  return since < 0.22 ? round3(1 + 0.04 * (1 - since / 0.22)) : 1;
}

/**
 * The pose of a lyric video at the time `t` (video seconds). The end card cover sits where the
 * shared end card of the theme puts it (`longVideoEndPose`; the Free watermark row changes it). Pure.
 */
export function lyricVideoPose(
  t: number,
  video: Pick<KitLyricVideo, 'states' | 'kickSecs'>,
  themeId: string,
  watermark: boolean
): LyricVideoPose {
  const spans = lyricVideoSpans(video.states);
  let current = 0;
  spans.forEach((span, index) => {
    if (span.fromSec <= t) {
      current = index;
    }
  });
  const now = spans[current] as StateSpan;
  const before = spans[current - 1];
  const share = before === undefined ? 1 : outCubic(progress(t, now.fromSec, now.fromSec + WIDE_STATE_MOVE_SEC));
  const to = layoutWeights(now.kind);
  const from = before === undefined ? to : layoutWeights(before.kind);
  const coverTo = coverOf(now.kind, themeId, watermark);
  const coverFrom = before === undefined ? coverTo : coverOf(before.kind, themeId, watermark);
  const next = spans[current + 1];
  // The dots count in the last 3 s of a title card or a rest.
  const endsAt = next?.fromSec ?? Number.POSITIVE_INFINITY;
  const dotsLit =
    (now.kind === 'title' || now.kind === 'rest') && Number.isFinite(endsAt) && t >= endsAt - WIDE_REST_DOTS
      ? Math.max(0, Math.min(WIDE_REST_DOTS, Math.floor(t - (endsAt - WIDE_REST_DOTS)) + 1))
      : 0;
  return {
    title: round3(lerp(from.title, to.title, share)),
    lyrics: round3(lerp(from.lyrics, to.lyrics, share)),
    end: round3(lerp(from.end, to.end, share)),
    cover: {
      x: round3(lerp(coverFrom.x, coverTo.x, share)),
      y: round3(lerp(coverFrom.y, coverTo.y, share)),
      size: round3(lerp(coverFrom.size, coverTo.size, share)),
      scale: wideKickScale(t, video.kickSecs),
    },
    dotsLit,
    state: now.kind,
  };
}

/** One fitted text of a 16:9 layout. */
export interface WideText {
  box: StoryBox;
  sizePx: number;
  lines: string[];
}

export interface LyricVideoTextsInput {
  video: Pick<KitLyricVideo, 'title' | 'artist' | 'kicker'>;
  themeId: string;
  fonts: { display: KitFontId; text: KitFontId };
  typeStyle: { display: ThemeTypeStyle & { scale: number }; text: ThemeTypeStyle };
  /** The style of the video (design y3). Absent: `card`. */
  style?: LyricVideoStyle;
}

/** The texts of the title card and the lyrics, fitted to their boxes (the title wraps on the title card). */
export interface LyricVideoTexts {
  title: { kicker: WideText; title: WideText; artist: WideText };
  lyrics: { title: WideText; artist: WideText };
  /** The size of the lyric before the fit to the lines: `lyricVideoLyricPx` of the style. */
  lyricMaxPx: number;
}

/**
 * The fitted texts of a lyric video: every text 40 px or more, the title of the title card at 150 px
 * times the display factor (172 px with no cover card), wrapped in its box. The boxes are those of
 * the style (`lyricVideoTextLayout`). The end card draws its texts itself (the shared end card). Pure.
 */
export function lyricVideoTexts(input: LyricVideoTextsInput): LyricVideoTexts {
  const frame = storyFrameOf(input.themeId);
  const k = frame.factors.display * input.typeStyle.display.scale;
  const display = { id: input.fonts.display, style: input.typeStyle.display };
  const text = { id: input.fonts.text, style: input.typeStyle.text };
  const fit = (
    value: string,
    font: { id: KitFontId; style: ThemeTypeStyle },
    sizePx: number,
    x: number,
    y: number,
    w: number,
    wrap: boolean
  ): WideText => {
    const fitted = fitStoryLine(value, font, sizePx, w, wrap);
    const size = Math.max(WIDE_MIN_TEXT_PX, fitted.sizePx);
    return { box: { x, y, w, h: round3(size * 1.1 * fitted.lines.length) }, sizePx: size, lines: fitted.lines };
  };
  const style = input.style ?? 'card';
  const layout = lyricVideoTextLayout(style);
  const t = layout.title;
  const kicker = fit(input.video.kicker, text, t.kickerPx, t.textX, t.textY, t.textW, false);
  const bigTitle = fit(input.video.title, display, t.titlePx * k, t.textX, t.textY + kicker.box.h + 24, t.textW, true);
  const bigArtist = fit(
    input.video.artist,
    text,
    t.artistPx,
    t.textX,
    bigTitle.box.y + bigTitle.box.h + 24,
    t.textW,
    false
  );
  const l = layout.lyrics;
  const smallTitle = fit(input.video.title, display, l.titlePx * k, l.textX, l.textY, l.textW, true);
  const smallArtist = fit(
    input.video.artist,
    text,
    l.artistPx,
    l.textX,
    smallTitle.box.y + smallTitle.box.h + 12,
    l.textW,
    false
  );
  return {
    title: { kicker, title: bigTitle, artist: bigArtist },
    lyrics: { title: smallTitle, artist: smallArtist },
    lyricMaxPx: lyricVideoLyricPx(style, k),
  };
}
